import theme from '../../assets/theme';
import {
  BlockQuoteNode,
  defaultNodeTypes,
  DeserializedNode,
  HeadingNode,
  InputNodeTypes,
  ItalicNode,
  LinkNode,
  ListItemNode,
  ListNode,
  MdastNode,
  OptionType,
  ParagraphNode,
  TextNode,
  ThematicBreakNode,
} from './pluginTypes';

export default function remarkSlatePlugin(opts?: OptionType) {
  const compiler = (node: { children: Array<MdastNode> }) => {
    return node.children.map(c => transform(c, opts));
  };

  // @ts-ignore
  this.Compiler = compiler;
}

export const getHeaderFontSize = (level: number, baseFontSize: number): number => {
  // Header font sizes based on level (h1 largest, h6 smallest)
  const sizeMultipliers: Record<number, number> = {
    1: 1.2, // h1 - largest
    2: 1.15, // h2
    3: 1.1, // h3
    4: 1.05, // h4
    5: 1.0, // h5
    6: 0.9, // h6 - smallest
  };
  return Math.round(baseFontSize * (sizeMultipliers[level] || 1));
};

// Recursively flatten all nodes to simple text nodes for listItem children
function flattenToTextNodes(
  nodes: Array<MdastNode>,
  opts?: OptionType,
): Array<{
  text: string;
  color?: string;
  bold?: boolean;
  italic?: boolean;
  underline?: boolean;
}> {
  const textNodes: Array<{
    text: string;
    color?: string;
    bold?: boolean;
    italic?: boolean;
    underline?: boolean;
  }> = [];

  function extractText(
    node: MdastNode,
    inheritedFormats: { bold?: boolean; italic?: boolean; underline?: boolean } = {},
  ): void {
    // Handle text nodes
    if (node.type === 'text') {
      textNodes.push({
        text: node.value ? `${node.value} ` : '',
        color: opts?.defaults?.color || theme.colors.black,
        bold: inheritedFormats.bold || undefined,
        italic: inheritedFormats.italic || undefined,
        underline: inheritedFormats.underline || undefined,
      });
      return;
    }
    if (node.type === 'break') {
      textNodes.push({
        text: '\n',
        color: opts?.defaults?.color || theme.colors.black,
        bold: inheritedFormats.bold || undefined,
        italic: inheritedFormats.italic || undefined,
        underline: inheritedFormats.underline || undefined,
      });
      return;
    }

    // Handle formatting nodes (strong, emphasis)
    const formats = { ...inheritedFormats };
    if (node.type === 'strong') {
      formats.bold = true;
    }
    if (node.type === 'emphasis') {
      formats.italic = true;
    }

    // For nodes with direct text value (like code, etc.)
    if (node.value && typeof node.value === 'string') {
      textNodes.push({
        text: `${node.value}`,
        color: opts?.defaults?.color || theme.colors.black,
        bold: formats.bold || undefined,
        italic: formats.italic || undefined,
        underline: formats.underline || undefined,
      });
      return;
    }

    // Recursively process children
    if (node.children && Array.isArray(node.children)) {
      node.children.forEach(child => extractText(child, formats));
      return;
    }

    // For nodes without children or value, add empty text
    if (!node.children && !node.value) {
      textNodes.push({
        text: '',
        color: opts?.defaults?.color || theme.colors.black,
        bold: formats.bold || undefined,
        italic: formats.italic || undefined,
        underline: formats.underline || undefined,
      });
    }
  }

  // Process each top-level node
  nodes.forEach(node => extractText(node));

  // Ensure we always have at least one text node
  if (textNodes.length === 0) {
    textNodes.push({
      text: '',
      color: opts?.defaults?.color || theme.colors.black,
      bold: undefined,
      italic: undefined,
      underline: undefined,
    });
  }

  return textNodes;
}

export function transform<T extends InputNodeTypes>(node: MdastNode, opts?: OptionType) {
  if (node.type === 'listItem') {
    // console.log('node', node);
  }
  const types = {
    ...defaultNodeTypes,
    heading: {
      ...defaultNodeTypes.heading,
    },
  };

  let children: Array<DeserializedNode<T>> = [{ text: '' }];

  const nodeChildren = node.children;
  if (nodeChildren && Array.isArray(nodeChildren) && nodeChildren.length > 0) {
    if (node.type === 'listItem') {
      // For listItem nodes, flatten all children to simple text nodes
      children = flattenToTextNodes(nodeChildren, opts);
    } else {
      children = nodeChildren.flatMap((c: MdastNode) =>
        transform(
          {
            ...c,
            ordered: node.ordered || false,
          },
          opts,
        ),
      );
    }
  }

  switch (node.type) {
    case 'heading':
      return {
        type: 'paragraph',
        size: getHeaderFontSize(node.depth || 1, opts?.defaults?.fontSize || 14),
        color: opts?.defaults?.color || theme.colors.black,
        align: opts?.defaults?.align || 'left',
        children,
      } as HeadingNode<T>;
    case 'list':
      return {
        type: node.ordered ? types.ol_list : types.ul_list,
        color: opts?.defaults?.color || theme.colors.black,
        size: opts?.defaults?.fontSize || 14,
        align: opts?.defaults?.align || 'left',
        children,
      } as ListNode<T>;
    case 'listItem':
      return {
        type: types.listItem,
        children,
        align: opts?.defaults?.align || 'left',
        color: opts?.defaults?.color || theme.colors.black,
        size: opts?.defaults?.fontSize || 14,
      } as ListItemNode<T>;
    case 'paragraph':
      return {
        type: types.paragraph,
        size: opts?.defaults?.fontSize || 14,
        color: opts?.defaults?.color || theme.colors.black,
        align: opts?.defaults?.align || 'left',
        children,
      } as ParagraphNode<T>;
    case 'link':
      return {
        type: types.link,
        color: opts?.defaults?.color || theme.colors.black,
        // size: opts?.defaults?.fontSize || 14,
        link: node.url,
        children,
      } as LinkNode<T>;
    // case 'image':
    //   return {
    //     type: types.image,
    //     children: [{ text: '' }],
    //     [imageSourceKey]: node.url,
    //     [imageCaptionKey]: node.alt,
    //   } as ImageNode<T>;
    case 'blockquote':
      return { type: types.paragraph, children } as BlockQuoteNode<T>;
    // case 'code':
    //   return {
    //     type: types.code_block,
    //     language: node.lang,
    //     children: [{ text: node.value }],
    //   } as CodeBlockNode<T>;

    case 'html':
      if (node.value?.includes('<br>')) {
        return {
          break: true,
          type: types.paragraph,
          size: opts?.defaults?.fontSize || 14,
          color: opts?.defaults?.color || theme.colors.black,
          align: opts?.defaults?.align || 'left',
          children: [{ text: node.value?.replace(/<br>/g, '') || '' }],
        } as ParagraphNode<T>;
      }
      return { type: 'paragraph', children: [{ text: node.value || '' }] };

    case 'emphasis':
      return ({
        [types.emphasis_mark as string]: true,
        ...forceLeafNode(children as Array<TextNode>),
        ...persistLeafFormats(children as Array<MdastNode>),
      } as unknown) as ItalicNode<T>;
    case 'strong':
      return {
        [types.strong_mark as string]: true,
        ...forceLeafNode(children as Array<TextNode>),
        ...persistLeafFormats(children as Array<MdastNode>),
      };
    // case 'delete':
    //   return {
    //     [types.delete_mark as string]: true,
    //     ...forceLeafNode(children as Array<TextNode>),
    //     ...persistLeafFormats(children as Array<MdastNode>),
    //   };
    // case 'inlineCode':
    //   return {
    //     [types.inline_code_mark as string]: true,
    //     text: node.value,
    //     ...persistLeafFormats(children as Array<MdastNode>),
    //   };
    case 'thematicBreak':
      return {
        type: types.paragraph,
        children: [{ text: '' }],
      } as ThematicBreakNode<T>;

    case 'text':
    default:
      return { text: node.value || '' };
  }
}

const forceLeafNode = (children: Array<TextNode>) => ({
  text: children.map(k => k?.text).join(''),
});

// This function is will take any unknown keys, and bring them up a level
// allowing leaf nodes to have many different formats at once
// for example, bold and italic on the same node
function persistLeafFormats(
  children: Array<MdastNode>,
): Omit<MdastNode, 'children' | 'type' | 'text'> {
  return children.reduce((acc, node) => {
    (Object.keys(node) as Array<keyof MdastNode>).forEach(function(key) {
      if (key === 'children' || key === 'type' || key === 'text') return;

      acc[key] = node[key];
    });

    return acc;
  }, {});
}
